/**
 * Graph Types for Workflow Agent
 *
 * These types represent the React Flow graph structure used in the UI.
 * They provide compile-time type safety for node data.
 *
 * Philosophy: Trust backend data. If backend sends malformed data,
 * let it fail fast rather than silently degrade.
 */

import { Edge, Node } from '@xyflow/react';

import { ParameterMap, StepExecutionStatus } from '@frontend/types/stepExecution';
import {
  ConditionalStep,
  EmailTrigger,
  ForLoopStep,
  MatchType,
  MessageTrigger,
  ScheduledTrigger,
  SpecializedAgentStep,
  Step,
  Trigger,
  WebhookTrigger,
  WorkflowStep,
} from '@frontend/types/workflow-agent';

// ============================================
// Common Types
// ============================================

/**
 * Represents the dimensions of a node in the workflow
 */
export type NodeDimensions = {
  width: number;
  height: number;
  minWidth?: number;
  minHeight?: number;
};

// ============================================
// UI-Extended Step Types
// ============================================

/**
 * Position type for UI nodes
 */
export type NodePosition = {
  x: number;
  y: number;
};

/**
 * Step with optional position (for nested ForLoop steps stored in React Flow)
 */
export type StepWithPosition = Step & {
  position?: NodePosition;
};

/**
 * ForLoopStep with UI-only properties for React Flow node data.
 * - start_step_id: tracks which nested step starts the loop execution (UI-only)
 * - steps: uses StepWithPosition for nested steps that may have embedded positions
 */
export type ForLoopStepUI = Omit<ForLoopStep, 'steps'> & {
  start_step_id?: string | null;
  steps: Record<string, StepWithPosition>;
};

// ============================================
// Node Type Constants
// ============================================

/**
 * All trigger node types
 */
export const TRIGGER_TYPES = ['webhook', 'scheduled', 'message', 'email'] as const;

/**
 * All step node types
 */
export const STEP_TYPES = ['specialized', 'conditional', 'for_loop', 'nested_workflow'] as const;

/**
 * All workflow node types (triggers + steps + UI-only)
 */
export const WORKFLOW_NODE_TYPES = [...TRIGGER_TYPES, ...STEP_TYPES, 'condition'] as const;

export type TriggerNodeType = (typeof TRIGGER_TYPES)[number];
export type StepNodeType = (typeof STEP_TYPES)[number];
export type WorkflowNodeType = (typeof WORKFLOW_NODE_TYPES)[number];

// ============================================
// Node Data Types
// ============================================

/**
 * Data for condition branch nodes.
 * These are UI-only nodes derived from ConditionalStep.conditions.
 * They are NOT persisted as steps in the backend.
 *
 * Note: matching_condition and match_type are now stored on edges (ConditionEdgeData),
 * not on the node itself. This enables one condition node with multiple outgoing edges.
 */
export type ConditionNodeData = {
  type: 'condition';
  name: string;
  parent_step_id: string;
  label?: string;
  /** @deprecated Legacy field — only used for backward-compatible rendering of old executions */
  matching_condition?: string;
  /** @deprecated Legacy field — only used for backward-compatible rendering of old executions */
  match_type?: MatchType;
};

/**
 * Data for placeholder nodes.
 * UI-only ephemeral nodes that serve as drop targets for condition branches.
 * Stripped before save — the backend is unaware of these.
 */
export type PlaceholderNodeData = {
  type: 'placeholder';
  name: 'placeholder';
};

// ============================================
// Edge Data Types
// ============================================

/**
 * Data for condition edges.
 * Stored in edge.data for edges from condition nodes to target steps.
 * This is the condition logic that was previously on ConditionNodeData.
 *
 * `id` is the backend `Condition.id` and lets the execution canvas attribute
 * `condition_result.evaluations` (keyed by `condition_id`) back to a specific
 * outgoing edge — required for per-edge "was this path taken" decisions when
 * multiple conditions on the same conditional share a `go_to_step_id`.
 */
export type ConditionEdgeData = {
  id?: string;
  matching_condition: string;
  match_type: MatchType;
  case_sensitive?: boolean;
};

/**
 * Data attached to edges during workflow execution to communicate runtime status.
 * Set by useStepExecutions, consumed by CustomEdge for stroke color rendering
 * and for the "X inputs mapped" pill.
 *
 * `mappedParameters` / `mappedAllExecutionParameters` are per-edge — for
 * condition edges with an `id`, they are filtered to the target executions
 * that were actually caused by traversing this edge (so an untaken edge ends
 * up with empty data and the pill is hidden, and sibling edges to the same
 * target don't show identical pills). For other edges they mirror the target
 * node's parameters (the only-possible-predecessor case).
 */
export type ExecutionEdgeData = {
  executionStatus?: StepExecutionStatus;
  mappedParameters?: ParameterMap;
  mappedAllExecutionParameters?: ParameterMap[];
};

/**
 * Generate a display label for a condition edge.
 * Used for edge labels on the canvas.
 */
export function getConditionEdgeLabel(data: ConditionEdgeData): string {
  if (data.match_type === MatchType.ELSE) {
    return 'ELSE';
  }
  return `${data.match_type} ${data.matching_condition}`;
}

/**
 * UI-specific properties that can be added to any node data
 */
export type NodeUIProps = {
  isUnsavedAsync?: boolean;
};

/**
 * Step node data with optional UI properties
 */
export type StepNodeData = (SpecializedAgentStep | ConditionalStep | ForLoopStep | WorkflowStep) &
  NodeUIProps;

/**
 * Trigger node data (uses existing Trigger union type)
 */
export type TriggerNodeData = Trigger;

/**
 * Union of all possible node data types in the workflow graph
 */
export type WorkflowNodeData =
  | StepNodeData
  | TriggerNodeData
  | ConditionNodeData
  | PlaceholderNodeData;

// ============================================
// Typed Node Variants
// ============================================

/**
 * React Flow's Node<Data, Type> allows TypeScript to narrow node.data
 * when checking node.type. Each node variant uses a literal type string
 * as the second generic parameter, enabling discriminated union narrowing.
 */

// Step nodes - each with literal type discriminator
export type ForLoopNode = Node<ForLoopStepUI & NodeUIProps, 'for_loop'>;
export type SpecializedNode = Node<SpecializedAgentStep & NodeUIProps, 'specialized'>;
export type ConditionalStepNode = Node<ConditionalStep & NodeUIProps, 'conditional'>;
export type NestedWorkflowNode = Node<WorkflowStep & NodeUIProps, 'nested_workflow'>;

// Trigger nodes - each with literal type discriminator
export type WebhookNode = Node<WebhookTrigger, 'webhook'>;
export type ScheduledNode = Node<ScheduledTrigger, 'scheduled'>;
export type MessageNode = Node<MessageTrigger, 'message'>;
export type EmailNode = Node<EmailTrigger, 'email'>;

// UI-only nodes
export type ConditionBranchNode = Node<ConditionNodeData, 'condition'>;
export type PlaceholderNode = Node<PlaceholderNodeData, 'placeholder'>;

// ============================================
// Node Union Types
// ============================================

/**
 * Union of all step node types.
 * Use type guards (isForLoopNode, etc.) to narrow to specific types.
 */
export type StepNode = ForLoopNode | SpecializedNode | ConditionalStepNode | NestedWorkflowNode;

/**
 * Union of all trigger node types.
 * Use type guards to narrow to specific trigger types.
 */
export type TriggerNode = WebhookNode | ScheduledNode | MessageNode | EmailNode;

/**
 * Union of all workflow node types.
 * This is the primary node type used throughout the workflow agent.
 *
 * When checking node.type === 'for_loop', TypeScript automatically
 * narrows node.data to ForLoopStep - no casts needed!
 */
export type WorkflowNode = StepNode | TriggerNode | ConditionBranchNode | PlaceholderNode;

// ============================================
// Typed Edge Variants
// ============================================

/**
 * Typed edge variants using React Flow generics.
 * Follows the same pattern as typed nodes (Node<Data, Type>).
 *
 * ConditionEdge: Edges from condition nodes carrying condition logic
 * RegularEdge: Standard workflow edges (between steps, triggers, etc.)
 */
export type ConditionEdge = Edge<ConditionEdgeData, 'condition'>;
export type RegularEdge = Edge<Record<string, never>, 'custom'>;

/**
 * A workflow edge - discriminated union of all edge types.
 * TypeScript automatically narrows edge.data when checking edge.type.
 */
export type WorkflowEdge = ConditionEdge | RegularEdge;

/**
 * The complete workflow graph representation for React Flow.
 */
export type WorkflowGraph = {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
};

// ============================================
// Type Guards - Node Types
// ============================================

/**
 * Check if a string is a trigger node type
 */
export function isTriggerNodeType(type: string | undefined): type is TriggerNodeType {
  return TRIGGER_TYPES.includes(type as TriggerNodeType);
}

/**
 * Check if a string is a step node type
 */
export function isStepNodeType(type: string | undefined): type is StepNodeType {
  return STEP_TYPES.includes(type as StepNodeType);
}

/**
 * Check if a string is a condition node type
 */
export function isConditionNodeType(type: string | undefined): type is 'condition' {
  return type === 'condition';
}

/**
 * Check if a string is any workflow node type
 */
export function isWorkflowNodeType(type: string | undefined): type is WorkflowNodeType {
  return WORKFLOW_NODE_TYPES.includes(type as WorkflowNodeType);
}

// ============================================
// Type Guards - Nodes (by node.type string)
// ============================================

/**
 * Check if a node is a trigger node.
 * Narrows to TriggerNode union (WebhookNode | ScheduledNode | MessageNode | EmailNode).
 */
export function isTriggerNode(node: Node): node is TriggerNode {
  return isTriggerNodeType(node.type);
}

/**
 * Check if a node is a step node.
 * Narrows to StepNode union (ForLoopNode | SpecializedNode | ConditionalStepNode | NestedWorkflowNode).
 */
export function isStepNode(node: Node): node is StepNode {
  return isStepNodeType(node.type);
}

/**
 * Check if a node is a condition branch node (UI-only).
 */
export function isConditionNode(node: Node): node is ConditionBranchNode {
  return isConditionNodeType(node.type);
}

/**
 * Check if a node is a for-loop node.
 * After this check, node.data is typed as ForLoopStep.
 */
export function isForLoopNode(node: Node): node is ForLoopNode {
  return node.type === 'for_loop';
}

/**
 * Check if a node is a conditional step node.
 * After this check, node.data is typed as ConditionalStep.
 */
export function isConditionalStepNode(node: Node): node is ConditionalStepNode {
  return node.type === 'conditional';
}

/**
 * Check if a node is a specialized agent node.
 * After this check, node.data is typed as SpecializedAgentStep.
 */
export function isSpecializedNode(node: Node): node is SpecializedNode {
  return node.type === 'specialized';
}

/**
 * Check if a node is a nested-workflow step node (delegates to another workflow).
 * After this check, node.data is typed as WorkflowStep.
 */
export function isNestedWorkflowNode(node: Node): node is NestedWorkflowNode {
  return node.type === 'nested_workflow';
}

/**
 * Check if a node is a webhook trigger node.
 */
export function isWebhookNode(node: Node): node is WebhookNode {
  return node.type === 'webhook';
}

/**
 * Check if a node is a scheduled trigger node.
 */
export function isScheduledNode(node: Node): node is ScheduledNode {
  return node.type === 'scheduled';
}

/**
 * Check if a node is a message trigger node.
 */
export function isMessageNode(node: Node): node is MessageNode {
  return node.type === 'message';
}

/**
 * Check if a node is an email trigger node.
 */
export function isEmailNode(node: Node): node is EmailNode {
  return node.type === 'email';
}

/**
 * Check if a node is a placeholder node (UI-only drop target).
 */
export function isPlaceholderNode(node: Node): node is PlaceholderNode {
  return node.type === 'placeholder';
}

// ============================================
// Type Guards - Edges
// ============================================

/**
 * Check if an edge is a condition edge.
 * After this check, edge.data is typed as ConditionEdgeData.
 */
export function isConditionEdge(edge: Edge): edge is ConditionEdge {
  return edge.type === 'condition';
}

/**
 * Check if an edge is a regular workflow edge.
 */
export function isRegularEdge(edge: Edge): edge is RegularEdge {
  return edge.type === 'custom' || !edge.type;
}
